/**
 * Downloads and install instructions.
 *
 * Binaries are served from the canonical download host behind a CDN, not from this
 * origin. The URLs are public because a binary without a valid entitlement does
 * nothing — the license, not the download, is what is sold.
 *
 * So the page is public (#404). A visitor who picked the Free plan reaches the install
 * command without an account; `chi auth login` is where the account comes in, and it
 * creates the free one on first use. Nothing here reads the session except the choice
 * of frame: the portal's rail for someone signed in, the public masthead for everyone
 * else — including the pre-render, which has no session, and the first client render,
 * which hydrates it before Firebase has answered.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { ReactNode } from 'react'

import { env } from '@/app/env'
import { useSession } from '@/app/providers'
import { capture } from '@/shared/lib/analytics'
import { AppShell, PageHead, PublicPage } from '@/shared/ui/Chrome'
import { Badge, Button, Label, Panel } from '@/shared/ui/primitives'

type Platform = 'macos' | 'linux' | 'windows'

/**
 * What we can actually install today.
 *
 * The previous version of this table advertised a Homebrew tap, a winget package and a
 * download host of its own — none of which exist. A page that prints a command which
 * fails is worse than a page that offers one option: the reader concludes the product is
 * broken, and they are not wrong to. The same rule is why every command here names
 * `archis.dev`: the old apex answers 503, so a line still pointing at it installs
 * nothing.
 *
 * So there is one command per platform, and it is the one that works.
 */
const INSTALL: Record<Platform, { label: string; primary: string; note: string }> = {
  macos: {
    label: 'macOS',
    primary: 'curl -fsSL https://archis.dev/install.sh | sh',
    note: 'arm64 and x86_64. The installer asks the API which build matches your machine, then refuses to install anything whose SHA-256 does not match what the API published.',
  },
  linux: {
    label: 'Linux',
    primary: 'curl -fsSL https://archis.dev/install.sh | sh',
    note: 'x86_64 and arm64, glibc. Installs to /usr/local/bin when writable and ~/.local/bin otherwise — it never asks for sudo.',
  },
  windows: {
    label: 'Windows',
    primary: 'Not yet available — use WSL for now.',
    note: 'The x86_64 Windows binary is built and published with every release; a native installer is not written yet. Inside WSL, the Linux command above works unchanged.',
  },
}

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'macos'
  const agent = navigator.userAgent
  if (/Win/i.test(agent)) return 'windows'
  if (/Mac/i.test(agent)) return 'macos'
  return 'linux'
}

/**
 * The portal's rail only when there is a portal to show: signed out, its Account links
 * all lead to /signin and its "Sign out" button signs out nobody.
 */
function Frame({ children }: { children: ReactNode }) {
  const { user } = useSession()
  if (user) return <AppShell>{children}</AppShell>
  return (
    <PublicPage>
      <section className="shell" style={{ paddingBlock: 'var(--s8) var(--s9)' }}>
        {children}
      </section>
    </PublicPage>
  )
}

export function Downloads() {
  const { user } = useSession()
  const [platform, setPlatform] = useState<Platform>(detectPlatform)
  const [copied, setCopied] = useState<string | null>(null)
  const active = INSTALL[platform]

  async function copy(command: string) {
    try {
      await navigator.clipboard.writeText(command)
      setCopied(command)
      capture('download_install_command_copied', { os: platform })
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard access can be denied; the command is visible and selectable
      // anyway, so there is nothing to recover from.
    }
  }

  function selectPlatform(next: Platform) {
    setPlatform(next)
    capture('download_os_selected', { os: next })
  }

  return (
    <Frame>
      <PageHead
        index="04"
        eyebrow="Downloads"
        title="Install Archis"
        subtitle="One command. The installer verifies every download against a checksum published by the API before it writes anything to disk."
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <Panel title="Install" index="01">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            <div className="row" style={{ gap: 0, border: '1px solid var(--rule-bright)', borderRadius: 'var(--radius)', width: 'fit-content' }}>
              {(Object.keys(INSTALL) as Platform[]).map((key) => (
                <button
                  key={key}
                  className="btn"
                  aria-pressed={platform === key}
                  onClick={() => selectPlatform(key)}
                  style={{
                    border: 'none',
                    borderRadius: 0,
                    background: platform === key ? 'var(--signal-wash)' : 'transparent',
                    color: platform === key ? 'var(--signal)' : 'var(--text-dim)',
                  }}
                >
                  {INSTALL[key].label}
                </button>
              ))}
            </div>

            {platform === 'windows' ? (
              <p className="dim" style={{ margin: 0 }}>
                {active.primary}
              </p>
            ) : (
              <div className="term">
                <div className="term__bar row row--between">
                  <span>install</span>
                  <Button size="sm" variant="ghost" onClick={() => void copy(active.primary)}>
                    {copied === active.primary ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <div className="term__body">
                  <div className="term__line">
                    <span className="term__prompt">$</span> {active.primary}
                  </div>
                </div>
              </div>
            )}

            <p className="mono-note" style={{ margin: 0 }}>
              {active.note}
            </p>
          </div>
        </Panel>

        <Panel title="First run" index="02">
          <div className="term">
            <div className="term__bar">terminal</div>
            <div className="term__body">
              <div className="term__line">
                <span className="term__prompt">$</span> chi auth login
              </div>
              <div className="term__line term__dim">{'  '}approve in the browser, then:</div>
              <div className="term__line">{'  '}</div>
              <div className="term__line">
                <span className="term__prompt">$</span> chi index .
              </div>
              <div className="term__line">
                <span className="term__prompt">$</span> chi agents on claude
              </div>
              <div className="term__line">
                <span className="term__prompt">$</span> chi agents check
              </div>
            </div>
          </div>
          {!user && (
            // Said here, not as a gate in front of the page: downloading needs no
            // account, and the first command is the one that asks for it.
            <p className="mono-note" style={{ margin: 'var(--s4) 0 0' }}>
              No account needed to download. <code>chi auth login</code> signs you in — or
              creates your free account — the first time you run it.{' '}
              <Link to="/pricing">What Free includes →</Link>
            </p>
          )}
        </Panel>

        <Panel title="Verify what you downloaded" index="03">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
            <p className="dim" style={{ margin: 0 }}>
              Every release ships <code>checksums.txt</code>, a release manifest signed with
              Ed25519, and an SBOM per artifact. The installer refuses an archive whose SHA-256
              does not match the manifest; <code>chi update</code> also checks the manifest
              signature against the key built into the binary before it replaces anything, and
              rolls back if the new binary fails its health check.{' '}
              <Link to="/verify#install">How to check it yourself →</Link>
            </p>
            <div className="row">
              <a
                href={`${env.downloadsBaseUrl}/releases/latest/checksums.txt`}
                onClick={() => capture('download_verify_link_clicked', { kind: 'checksums' })}
              >
                <Button size="sm">checksums.txt</Button>
              </a>
              <a
                href={`${env.downloadsBaseUrl}/releases/latest/release-manifest.json`}
                onClick={() => capture('download_verify_link_clicked', { kind: 'manifest' })}
              >
                <Button size="sm" variant="ghost">
                  release manifest
                </Button>
              </a>
              <a
                href="/v1/entitlements/keys"
                onClick={() => capture('download_verify_link_clicked', { kind: 'keys' })}
              >
                <Button size="sm" variant="ghost">
                  licence public keys
                </Button>
              </a>
            </div>
          </div>
        </Panel>

        <Panel title="Update channels" index="04">
          <div className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
            <div className="row row--between">
              <Label index="S">stable</Label>
              <Badge tone="signal">recommended</Badge>
            </div>
            <p className="mono-note" style={{ margin: 0 }}>
              <code>chi update --channel stable</code> — production releases only.
            </p>
            <div className="ruler" style={{ margin: 'var(--s2) 0' }} />
            <Label index="B">beta</Label>
            <p className="mono-note" style={{ margin: 0 }}>
              <code>chi update --channel beta</code> — early access to new language
              frontends and integrations. Team administrators can pin the channel for the
              whole organization from Policies.
            </p>
            <div className="ruler" style={{ margin: 'var(--s2) 0' }} />
            {/* The reader deciding which channel to be on is the reader who wants to
                know what shipped. It is the only place on the portal where that
                question is already being asked. */}
            <p className="mono-note" style={{ margin: 0 }}>
              <Link to="/changelog">Changelog</Link> — every released version and what it
              changed.
            </p>
          </div>
        </Panel>
      </div>
    </Frame>
  )
}
